import React, { useEffect, useState } from "react";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import InputField from "../shared-components/InputField";
import Divider from "../shared-components/LineDivider";
import { uniqueUUID } from "../utils";
import { PersonFormStateError } from "./PersonFormErrorState";
import { NameType } from "../api/Person";

export interface AliasesDefinitions {
  firstName: string;
  middleName: string;
  lastName: string;
  key: string;
  name_type: NameType;
}

type Props = {
  onChange?: (aliases: AliasesDefinitions[]) => void;
  prefill?: AliasesDefinitions[];
  error?: PersonFormStateError;
  alternateColor?: boolean;
};

function buildEmptyDefinition(isMain: boolean = false): AliasesDefinitions {
  return {
    firstName: "",
    middleName: "",
    lastName: "",
    key: uniqueUUID(),
    name_type: isMain ? NameType.MAIN : NameType.ALIAS,
  };
}

const PersonFormAliases = ({
  onChange,
  error,
  prefill,
  alternateColor,
}: Props) => {
  const [fieldsList, setFields] = useState<AliasesDefinitions[]>([
    buildEmptyDefinition(),
  ]);
  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (
    idx: number,
    field: "firstName" | "middleName" | "lastName",
    value: string
  ) => {
    fieldsList[idx][field] = value;
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: AliasesDefinitions[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    idx: number
  ) => {
    e.preventDefault();
    removeField(idx);
  };

  useEffect(() => {
    if (prefill && prefill.length > 0) {
      handleChange([...prefill]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  return (
    <>
      {fieldsList.map((fields, idx) => (
        <div key={fields.key} className="flex flex-wrap">
          <InputField
            handleChange={(e) => {
              updateField(idx, "firstName", e.target.value);
            }}
            name={`first-name-${idx}`}
            placeholder=""
            label={"First Name*"}
            classes="mb-4 mr-8 border-rose-600"
            value={fields.firstName}
            className={`${
              error?.names?.first_name?.[idx] ? "border-rose-600" : ""
            }`}
          />

          <InputField
            handleChange={(e) => {
              updateField(idx, "middleName", e.target.value);
            }}
            name={`middle-name-${idx}`}
            placeholder=""
            label={"Middle Name"}
            value={fields.middleName}
            classes={"mb-4 mr-8"}
          />
          <InputField
            handleChange={(e) => {
              updateField(idx, "lastName", e.target.value);
            }}
            name={`last-name-${idx}`}
            placeholder=""
            label={"Last Name*"}
            value={fields.lastName}
            className={`${
              error?.names?.last_name?.[idx] ? "border-rose-600" : ""
            }`}
          />

          {idx > 0 && (
            <div className="w-full">
              <Button
                type={ButtonTypes.remove}
                text="Remove"
                colorType={ButtonColorTypes.negativeAction}
                onClick={(e) => handleRemove(e, idx)}
                className="mt-4 px-0"
              />
            </div>
          )}
          <Divider className="mb-6" />
        </div>
      ))}
      <Button
        type={ButtonTypes.add}
        text="Add Aliases"
        colorType={
          alternateColor ? ButtonColorTypes.alternate : ButtonColorTypes.action
        }
        onClick={handleAdd}
      />
    </>
  );
};

export default PersonFormAliases;
